import { Box, Stack, Typography } from '@mui/material';
import type { GridRowParams, GridRowSelectionModel } from '@mui/x-data-grid-pro';
import { selectPlanTemplateDialogSelectors } from '@vy/app-selectors';
import type { PlanOverview } from '@vy/api-definition';
import { isNotEmptyString } from '@vy/utils';
import React, { useMemo } from 'react';
import { VyTable } from '../../../../../../../components/new-design/table/vy-table/VyTable';
import { VyTableToolbar } from '../../../../../../../components/new-design/table/vy-table-toolbar/vy-table-toolbar';
import { SELECT_PLAN_TEMPLATE_COLUMNS } from '../../select-plan-template-dialog-consts/select-plan-template-columns';

type SelectPlanTemplateDialogContentProps = {
  templates: PlanOverview[];
  itemsCount: number;
  isLoading: boolean;
  searchValue: string;
  onSearchChange: (value: string) => void;
  selectedPlanId: string | null;
  onSelectionChange: (planId: string | null) => void;
  page: number;
  pageSize: number;
  onPageChange: (page: number) => void;
  onPageSizeChange: (size: number) => void;
};

type NoRowsOverlayProps = {
  message: string;
};

const NoRowsOverlay: React.FC<NoRowsOverlayProps> = ({ message }) => (
  <Stack sx={{ alignItems: 'center', justifyContent: 'center', height: '100%' }}>
    <Typography variant="body2" sx={{
      color: "text.secondary"
    }}>
      {message}
    </Typography>
  </Stack>
);

export const SelectPlanTemplateDialogContent: React.FC<SelectPlanTemplateDialogContentProps> = ({
  templates,
  itemsCount,
  isLoading,
  searchValue,
  onSearchChange,
  selectedPlanId,
  onSelectionChange,
  page,
  pageSize,
  onPageChange,
  onPageSizeChange,
}) => {
  const rowSelectionModel = useMemo<GridRowSelectionModel>(
    () => ({ type: 'include', ids: new Set(selectedPlanId ? [selectedPlanId] : []) }),
    [selectedPlanId],
  );

  const emptyMessage = isNotEmptyString(searchValue)
    ? 'No templates match your search.'
    : 'No templates available';

  const noRowsOverlay = useMemo(
    () =>
      (function NoRows() {
        return <NoRowsOverlay message={emptyMessage} />;
      }),
    [emptyMessage],
  );

  const handleSelectionModelChange = (model: GridRowSelectionModel) => {
    const lastId = Array.from(model.ids).at(-1);
    onSelectionChange(lastId !== undefined ? String(lastId) : null);
  };

  const handleRowClick = (params: GridRowParams) => {
    onSelectionChange(selectedPlanId === params.id ? null : String(params.id));
  };

  return (
    <Stack spacing={1} sx={{ minHeight: '200px', height: '600px' }}>
      <VyTableToolbar sx={{ padding: 0 }}>
        <VyTableToolbar.Queries>
          <VyTableToolbar.SearchInput
            value={searchValue}
            onChange={(event) => onSearchChange(event.target.value)}
            placeholder="Plan name"
            size="medium"
            showLabel={false}
            slotProps={{
              htmlInput: { 'data-vy': selectPlanTemplateDialogSelectors.searchInput },
            }}
          />
        </VyTableToolbar.Queries>
      </VyTableToolbar>

      <Box sx={{ flex: 1, minHeight: 0 }}>
        <VyTable
          tableName="select-plan-template"
          columns={SELECT_PLAN_TEMPLATE_COLUMNS}
          items={templates}
          itemsCount={itemsCount}
          loadingItems={isLoading}
          checkboxSelection
          disableMultipleRowSelection
          rowSelectionModel={rowSelectionModel}
          onRowSelectionModelChange={handleSelectionModelChange}
          onRowClick={handleRowClick}
          paginationMode="server"
          page={page}
          pageSize={pageSize}
          onPageChange={onPageChange}
          onPageSizeChange={onPageSizeChange}
          emptyTableMessageSize={14}
          noRowsOverlay={noRowsOverlay}
          slotProps={{
            row: { 'data-vy': selectPlanTemplateDialogSelectors.tableRow },
          }}
          sx={{
            '& .MuiDataGrid-columnHeaderCheckbox .MuiCheckbox-root': { display: 'none' },
          }}
        />
      </Box>
    </Stack>
  );
};
